import React, { useState, useRef, useEffect } from 'react';
import { Plus, X, Trash2, MessageCircleQuestion } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Las preguntas que el negocio se inventa.
 *
 * QUÉ RESUELVE
 * El formulario de "Qué contesta" son las preguntas que sabemos que se hacen en
 * cualquier local. Las que se hacen en ESTE local no las sabe nadie más que el
 * mostrador: si vendés tortas, te preguntan si hacés sin TACC; si sos
 * veterinaria, si atendés urgencias de noche. Antes eso terminaba todo junto en
 * el campo "lo que siempre preguntan", que es un párrafo suelto: el buscador lo
 * devuelve entero y la persona recibe cinco respuestas para una pregunta.
 *
 * DISPARADOR Y RESPUESTA, NO PREGUNTA Y RESPUESTA
 * Lo que se carga no es cómo la escribiríamos nosotros, es cómo la escribe la
 * gente. El asistente busca por parecido de palabras: "¿hacen tortas sin TACC?"
 * y "son celíaco friendly?" no comparten casi ninguna, así que cada forma de
 * preguntar lo mismo suma. Por eso son varias por pregunta y por eso el campo
 * dice "cuando te preguntan" y no "pregunta".
 *
 * LAS CATEGORÍAS NO VIAJAN
 * Agrupan acá adentro y nada más. Al asistente cada pregunta le llega por
 * separado, que es lo que hace que preguntar por la cochera devuelva la
 * respuesta de la cochera y no todo lo que el negocio cargó ese día.
 */

/**
 * Un id que el backend acepta: minúsculas, números y guiones.
 *
 * Se exporta porque la pantalla de al lado también crea preguntas —la que
 * arranca desde "te preguntaron esto y el asistente no supo"— y dos generadores
 * distintos son dos formas distintas de romper la validación del backend.
 */
export const nuevoId = (prefijo) => `${prefijo}-${Math.random().toString(36).slice(2, 10)}`;

/** Una forma de preguntar, con su cruz para sacarla. */
function Disparador({ texto, onSacar }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-slate-100 py-1 pl-2.5 pr-1 text-[0.78rem] text-slate-700">
      <span className="min-w-0 truncate">{texto}</span>
      <button
        type="button"
        onClick={onSacar}
        aria-label={`Sacar "${texto}"`}
        className="shrink-0 rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

/** Una pregunta: sus disparadores, su respuesta y a qué categoría va. */
function Pregunta({ pregunta, categorias, limites, enfocar, onCambio, onBorrar }) {
  const [borrador, setBorrador] = useState('');
  const respuestaRef = useRef(null);

  // Una pregunta recién creada desde "te preguntaron esto" ya trae el
  // disparador puesto: lo único que falta es la respuesta, así que el cursor
  // arranca ahí y no obliga a buscar el campo.
  useEffect(() => {
    if (enfocar && respuestaRef.current) {
      respuestaRef.current.focus();
      respuestaRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [enfocar]);

  const agregarDisparador = () => {
    const limpio = borrador.trim();
    if (!limpio) return;
    if (pregunta.disparadores.includes(limpio)) { setBorrador(''); return; }
    if (pregunta.disparadores.length >= limites.disparadoresPorPregunta) return;
    onCambio({ ...pregunta, disparadores: [...pregunta.disparadores, limpio] });
    setBorrador('');
  };

  const lleno = pregunta.disparadores.length >= limites.disparadoresPorPregunta;

  return (
    <article className="rounded-xl border border-slate-200 bg-white p-3">
      <p className="m-0 text-[0.78rem] font-semibold uppercase tracking-wide text-slate-400">
        Cuando te preguntan
      </p>

      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        {pregunta.disparadores.map((d) => (
          <Disparador
            key={d}
            texto={d}
            onSacar={() => onCambio({
              ...pregunta,
              disparadores: pregunta.disparadores.filter((otro) => otro !== d),
            })}
          />
        ))}
      </div>

      {!lleno && (
        <div className="mt-1.5 flex items-center gap-1.5">
          <input
            type="text"
            value={borrador}
            onChange={(e) => setBorrador(e.target.value)}
            onBlur={agregarDisparador}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); agregarDisparador(); }
            }}
            maxLength={limites.largoDisparador}
            placeholder={pregunta.disparadores.length === 0
              ? '¿hacen tortas sin TACC?'
              : 'otra forma de preguntar lo mismo'}
            className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-[0.82rem] text-slate-800 outline-none placeholder:text-slate-300 focus:border-slate-400"
          />
          <button
            type="button"
            onClick={agregarDisparador}
            disabled={!borrador.trim()}
            className="shrink-0 rounded-lg border border-slate-300 p-1.5 text-slate-500 hover:bg-slate-50 disabled:opacity-40"
            aria-label="Agregar esta forma de preguntar"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <p className="m-0 mt-3 text-[0.78rem] font-semibold uppercase tracking-wide text-slate-400">
        El asistente contesta
      </p>
      <textarea
        ref={respuestaRef}
        rows={2}
        value={pregunta.respuesta}
        onChange={(e) => onCambio({ ...pregunta, respuesta: e.target.value })}
        maxLength={limites.largoRespuesta}
        placeholder="Escribilo como se lo dirías por WhatsApp."
        className="mt-1.5 w-full resize-y rounded-lg border border-slate-300 px-3 py-2 text-[0.85rem] text-slate-800 outline-none placeholder:text-slate-300 focus:border-slate-400"
      />

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {categorias.length > 0 && (
          <select
            value={pregunta.categoriaId || ''}
            onChange={(e) => onCambio({ ...pregunta, categoriaId: e.target.value || null })}
            className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-[0.78rem] text-slate-600 outline-none focus:border-slate-400"
          >
            <option value="">Sin categoría</option>
            {categorias.map((c) => (
              <option key={c.id} value={c.id}>{c.titulo}</option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={onBorrar}
          className="ml-auto inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[0.78rem] text-slate-400 hover:bg-red-50 hover:text-red-600"
        >
          <Trash2 className="h-3.5 w-3.5" /> Borrar
        </button>
      </div>
    </article>
  );
}

const PreguntasPropiasWhatsApp = ({ propias, limites, foco, onCambio, onFocoTomado }) => {
  const categorias = propias.categorias || [];
  const preguntas = propias.preguntas || [];

  useEffect(() => {
    if (foco) onFocoTomado();
  }, [foco, onFocoTomado]);

  const cambiarPregunta = (nueva) => onCambio({
    ...propias,
    preguntas: preguntas.map((p) => (p.id === nueva.id ? nueva : p)),
  });

  const borrarPregunta = (id) => onCambio({
    ...propias,
    preguntas: preguntas.filter((p) => p.id !== id),
  });

  const agregarPregunta = (categoriaId = null) => onCambio({
    ...propias,
    preguntas: [
      ...preguntas,
      { id: nuevoId('p'), categoriaId, disparadores: [], respuesta: '' },
    ],
  });

  const agregarCategoria = () => onCambio({
    ...propias,
    categorias: [...categorias, { id: nuevoId('c'), titulo: '' }],
  });

  const cambiarCategoria = (id, titulo) => onCambio({
    ...propias,
    categorias: categorias.map((c) => (c.id === id ? { ...c, titulo } : c)),
  });

  // Borrar una categoría no borra su trabajo: las preguntas pasan a "Sin
  // categoría", que es lo que alguien espera y lo que evita que un clic se
  // lleve puestas diez respuestas escritas a mano.
  const borrarCategoria = (id) => onCambio({
    ...propias,
    categorias: categorias.filter((c) => c.id !== id),
    preguntas: preguntas.map((p) => (p.categoriaId === id ? { ...p, categoriaId: null } : p)),
  });

  const sueltas = preguntas.filter((p) => !categorias.some((c) => c.id === p.categoriaId));
  const lleno = preguntas.length >= limites.preguntas;

  const bloque = (titulo, lista, categoriaId) => (
    <div className="grid gap-2 sm:grid-cols-2">
      {lista.map((p) => (
        <Pregunta
          key={p.id}
          pregunta={p}
          categorias={categorias}
          limites={limites}
          enfocar={foco === p.id}
          onCambio={cambiarPregunta}
          onBorrar={() => borrarPregunta(p.id)}
        />
      ))}
      {!lleno && (
        <button
          type="button"
          onClick={() => agregarPregunta(categoriaId)}
          className="flex min-h-[7rem] items-center justify-center gap-1.5 rounded-xl border border-dashed border-slate-300 text-[0.82rem] font-semibold text-slate-500 hover:border-slate-400 hover:bg-slate-50"
        >
          <Plus className="h-4 w-4" /> Agregar pregunta
          {titulo ? <span className="sr-only"> a {titulo}</span> : null}
        </button>
      )}
    </div>
  );

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4">
      <h3 className="m-0 flex items-center gap-2 text-[0.95rem] font-bold text-slate-900">
        <MessageCircleQuestion className="h-4 w-4 text-slate-400" />
        Tus preguntas
      </h3>
      <p className="m-0 mt-0.5 max-w-[80ch] text-[0.78rem] leading-snug text-slate-400">
        Lo que te preguntan a vos y no está arriba. Cargá cómo lo escribe la
        gente —cuantas más formas, mejor lo encuentra— y qué querés que conteste.
        Las categorías son para ordenarte acá: el asistente recibe cada pregunta
        por separado.
      </p>

      <div className="mt-3 flex flex-col gap-4">
        {categorias.map((c) => (
          <div key={c.id}>
            <div className="mb-2 flex items-center gap-2">
              <input
                type="text"
                value={c.titulo}
                onChange={(e) => cambiarCategoria(c.id, e.target.value)}
                maxLength={limites.largoTitulo}
                placeholder="Nombre de la categoría"
                className="min-w-0 flex-1 rounded-lg border border-transparent bg-slate-50 px-2.5 py-1 text-[0.85rem] font-semibold text-slate-800 outline-none placeholder:font-normal placeholder:text-slate-300 hover:border-slate-200 focus:border-slate-400 focus:bg-white"
              />
              <button
                type="button"
                onClick={() => borrarCategoria(c.id)}
                className="shrink-0 rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                aria-label={`Borrar la categoría ${c.titulo || 'sin nombre'}`}
                title="Las preguntas no se borran: quedan sin categoría"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            {bloque(c.titulo, preguntas.filter((p) => p.categoriaId === c.id), c.id)}
          </div>
        ))}

        {(sueltas.length > 0 || categorias.length === 0) && (
          <div>
            {categorias.length > 0 && (
              <p className="m-0 mb-2 px-2.5 text-[0.85rem] font-semibold text-slate-400">
                Sin categoría
              </p>
            )}
            {bloque(null, sueltas, null)}
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        {categorias.length < limites.categorias && (
          <button
            type="button"
            onClick={agregarCategoria}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5',
              'text-[0.82rem] font-semibold text-slate-600 hover:bg-slate-50',
            )}
          >
            <Plus className="h-3.5 w-3.5" /> Agregar categoría
          </button>
        )}
        {lleno && (
          <span className="text-[0.78rem] text-slate-400">
            Llegaste a {limites.preguntas} preguntas, que es el máximo.
          </span>
        )}
      </div>
    </section>
  );
};

export default PreguntasPropiasWhatsApp;
